import { useEffect, useRef } from 'react';
import { Link } from '../hooks/useRouter';
import { STACK_LAYERS } from '../content/visuals';

export default function PerceptionStack() {
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) element.classList.add('is-visible');
    }, { threshold: 0.12 });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bv-perception-section" aria-labelledby="stack-title">
      <div className="bv-wrap">
        <div ref={ref} className="bv-perception-panel">
          <header className="bv-platform-heading">
            <p className="bv-eyebrow">Black Vanilla Perception</p>
            <h2 id="stack-title" className="bv-h2">One perception stack. Multiple physical-world applications.</h2>
            <p className="bv-perception-lead">
              We fuse sensing, compute and AI into a unified perception layer that works across environments, platforms and industries.
            </p>
            <Link to="/technology" className="bv-btn bv-btn-on-dark">
              Explore our technology <span aria-hidden="true">→</span>
            </Link>
          </header>

          <div className="bv-stack-layout">
            <article className="bv-stack-side">
              <span className="bv-stack-icon" aria-hidden="true">
                <svg viewBox="0 0 48 48">
                  <rect x="10" y="14" width="28" height="20" rx="3" fill="none" stroke="currentColor" strokeWidth="1.6" />
                  <path d="M16 20h16M16 26h10" stroke="currentColor" strokeWidth="1.4" />
                  <circle cx="32" cy="26" r="2" fill="currentColor" />
                </svg>
              </span>
              <h3>Edge</h3>
              <p>Jetson / ROS 2 real-time perception</p>
            </article>

            <ol className="bv-stack-layers">
              {STACK_LAYERS.map((layer, index) => (
                <li key={layer.title} style={{ '--i': index }}>
                  <b>{String(index + 1).padStart(2, '0')}</b>
                  <div>
                    <strong>{layer.title}</strong>
                    <span>{layer.body}</span>
                  </div>
                </li>
              ))}
            </ol>

            <article className="bv-stack-side">
              <span className="bv-stack-icon" aria-hidden="true">
                <svg viewBox="0 0 48 48">
                  <path d="M14 30h20a8 8 0 0 0 1.4-15.8 12 12 0 0 0-22.4-3.2A9 9 0 0 0 14 30z" fill="none" stroke="currentColor" strokeWidth="1.6" />
                </svg>
              </span>
              <h3>Cloud</h3>
              <p>High-quality reconstruction and spatial processing</p>
            </article>
          </div>

          <svg className="bv-stack-globe" viewBox="0 0 220 220" aria-hidden="true">
            <circle cx="110" cy="110" r="86" fill="none" stroke="currentColor" />
            <ellipse cx="110" cy="110" rx="86" ry="34" fill="none" stroke="currentColor" />
            <ellipse cx="110" cy="110" rx="34" ry="86" fill="none" stroke="currentColor" />
            <path d="M24 110h172M110 24v172" fill="none" stroke="currentColor" />
            {Array.from({ length: 18 }, (_, i) => (
              <circle key={i} cx={110 + Math.cos(i * 0.7) * (40 + (i % 5) * 10)} cy={110 + Math.sin(i * 1.1) * (36 + (i % 4) * 12)} r="1.6" fill="currentColor" />
            ))}
          </svg>
        </div>
      </div>
    </section>
  );
}
